import type { CatalogApproval, Scan, ScanReviewGroup } from "@hobbyshopify/backend-client";
import { hasChoice, selectedProductId } from "./candidates";
import { catalogApproval, codeKey, defaultApprovedCodes } from "./catalog";
import { centsToInput, inputToCents } from "./scan-edit";

/** What the reviewer changed on a row before confirming it. Absent fields keep the row's own. */
export interface ReviewDraft {
  readonly price?: string;
  readonly quantity?: number;
  /** Whether the row's codes are approved as naming this item. */
  readonly approveCodes?: boolean;
}

/** What one row confirms with. */
export interface ReviewEntry {
  readonly group: ScanReviewGroup;
  /** The price to set; null keeps the catalogued card's own, so a confirm writes no price history. */
  readonly priceCents: number | null;
  /** Copies to stock; null stocks nothing (a customer's scan, or a card the shop does not count). */
  readonly quantity: number | null;
  /** The row's codes as the shop's catalogue vote; null casts none. */
  readonly catalogApproval: CatalogApproval | null;
}

/**
 * The drafts of the rows still listed; the same object when every draft's row is.
 *
 * A draft lives as long as its row: a row a confirm took, whole or partway
 * through a batch, or that went elsewhere, takes its typed price and count with
 * it, so a later scan of the same card starts from its own figures.
 */
export function liveDrafts(
  drafts: Readonly<Record<string, ReviewDraft>>,
  keys: Iterable<string>,
): Readonly<Record<string, ReviewDraft>> {
  const listed = new Set(keys);
  const gone = Object.keys(drafts).filter((key) => !listed.has(key));
  if (gone.length === 0) return drafts;
  const next = { ...drafts };
  for (const key of gone) delete next[key];
  return next;
}

/** The drafts less those of the rows an action settled; the same object when none had one. */
export function withoutDrafts(
  drafts: Readonly<Record<string, ReviewDraft>>,
  keys: readonly string[],
): Readonly<Record<string, ReviewDraft>> {
  return liveDrafts(
    drafts,
    Object.keys(drafts).filter((key) => !keys.includes(key)),
  );
}

/** Under "Couldn't load …" when a read fails on its way (a 5xx, offline) rather than with the server's sentence. */
export const READ_FAILED_ON_THE_WAY = "Something went wrong on the way. Try again.";

/**
 * How long a scan that has its reading may sit on one stage before it counts as
 * landed: the backend's orphan sweep waits as long by default
 * (`SCAN_ORPHAN_REJECT_MINUTES`) before it rejects a scan with no reading.
 */
const SCAN_STUCK_AFTER_MS = 5 * 60_000;

/**
 * Still in the pipeline: a pending scan whose recorded progress has not reached
 * `scan.completed`, or one from before progress was recorded with no reading
 * yet. Not a review row until it lands, as on iOS (`AdminScan.isScanning`).
 *
 * Unlike iOS, a scan with its reading that has sat on one stage for five
 * minutes is a row again: its pipeline died (a deploy mid-scan), and the
 * backend keeps such a scan to be reviewed as it is.
 */
export function isScanning(
  scan: Pick<Scan, "review" | "scanProgress" | "scanProgressAt" | "cardInfo" | "sealedInfo">,
  now: Date,
): boolean {
  if (scan.review !== "PENDING") return false;
  const unread = scan.cardInfo == null && scan.sealedInfo == null;
  if (scan.scanProgress === null) return unread;
  if (scan.scanProgress === "scan.completed") return false;
  const since = scan.scanProgressAt === null ? 0 : now.getTime() - scan.scanProgressAt.getTime();
  return unread || since < SCAN_STUCK_AFTER_MS;
}

/** What waits on Review, counted as Review lists it: the copies, and no card still in the pipeline. */
export const reviewWaiting = (groups: readonly ScanReviewGroup[], now: Date) =>
  groups.filter((g) => !isScanning(g.sample, now)).reduce((n, g) => n + g.count, 0);

/** A customer's confirmed scans, the ones they can buy; pending ones are review rows and rejected ones are not shown. */
export const confirmedScans = (scans: readonly Scan[]) => scans.filter((s) => s.review === "CONFIRMED");

/** A customer's scan is a price question, not stock coming in. */
export const isCustomerGroup = (group: ScanReviewGroup) => group.sample.kind === "CUSTOMER";

/** The shop's own price when the card is catalogued, else the pipeline's estimate. */
export function reviewPriceText(group: ScanReviewGroup, draft: ReviewDraft | undefined): string {
  return draft?.price ?? centsToInput(group.card?.currentPriceCents ?? group.sample.estimatedPriceCents);
}

/** The group's own count until changed; zero confirms the card untracked. */
export function reviewQuantity(group: ScanReviewGroup, draft: ReviewDraft | undefined): number {
  return draft?.quantity ?? Math.max(1, group.count);
}

/** The lookup weighed products and picked none, so a confirm would price from nothing the reviewer chose. */
export function needsCandidatePick(scan: Pick<Scan, "candidates" | "scpData">): boolean {
  return scan.candidates.length > 0 && selectedProductId(scan.scpData) === null;
}

/** Look-alikes worth showing on the row. */
export function hasCandidateChoice(scan: Pick<Scan, "candidates" | "scpData">): boolean {
  return hasChoice(scan.candidates, scan.scpData);
}

/** Shops disagree about the row's code: settled in the editor, never from the row. */
export const isCatalogDisputed = (group: ScanReviewGroup) => group.sample.catalog?.status === "CONFLICT";

/** Ticked, every code on the row; untouched, only what the shop's own device read. */
function reviewApprovedCodes(group: ScanReviewGroup, draft: ReviewDraft | undefined): string[] {
  if (draft?.approveCodes === undefined) return defaultApprovedCodes(group.sample);
  return draft.approveCodes ? group.sample.suggestedCodes.map(codeKey) : [];
}

export function reviewApprovesCodes(group: ScanReviewGroup, draft: ReviewDraft | undefined): boolean {
  return reviewApprovedCodes(group, draft).length > 0;
}

/**
 * One customer's pending scans as review rows.
 *
 * The queue groups a card across every customer who scanned it, and a row here
 * confirms what it holds, so each group is cut down to this customer's scans,
 * counted again and shown with their photograph. A pending scan the queue has
 * not grouped yet stands alone; one still in the pipeline is no row yet, and
 * one no card was recognised in is none at all: the queue leaves it out, it
 * cannot be reviewed, and the page lists it apart. Rows keep the order of the
 * customer's scans. A port of the iOS
 * `AdminCustomerScansOps.reviewGroups(for:queue:)`.
 */
export function customerReviewGroups(
  scans: readonly Scan[],
  queue: readonly ScanReviewGroup[],
  now: Date,
): ScanReviewGroup[] {
  const pending = scans.filter(
    (s) => s.review === "PENDING" && s.recognitionOutcome !== "no_match" && !isScanning(s, now),
  );
  const covered = new Set<string>();
  const groups: ScanReviewGroup[] = [];

  for (const group of queue) {
    const mine = pending.filter((s) => group.scanIds.includes(s.id) && !covered.has(s.id));
    if (mine.length === 0) continue;
    const scanIds = mine.map((s) => s.id);
    for (const id of scanIds) covered.add(id);
    groups.push({ ...group, scanIds, count: scanIds.length, sample: mine[0] });
  }

  for (const scan of pending) {
    if (covered.has(scan.id)) continue;
    const matched = scan.matchKind === "PRODUCT" || scan.matchKind === "IDENTITY";
    groups.push({
      key: scan.id,
      bucket: matched ? "exists" : "new",
      scanIds: [scan.id],
      count: 1,
      sample: scan,
      card: null,
      differences: [],
    });
  }

  const order = new Map(pending.map((s, i) => [s.id, i]));
  return groups.sort((a, b) => (order.get(a.sample.id) ?? 0) - (order.get(b.sample.id) ?? 0));
}

/**
 * What a customer's row is known by: its oldest scan, which stays put while newer
 * copies of the card join the row or land from the pipeline. The queue's key is
 * not steady here: a scan read before the queue grouped it stands alone under
 * its own id, then joins a group. The typed price, busy and picking hang on it.
 */
export const customerRowKey = (group: ScanReviewGroup) => group.scanIds[group.scanIds.length - 1];

/**
 * A customer's drafts after a read, from the rows shown before it (`before`) to
 * the rows it lists. A row takes the draft typed on the row its oldest scan with
 * one stood in (its own, when the read moved nothing), as a row is known by its
 * oldest scan, so a price follows its scans wherever the read moved them: a lone
 * scan grouped, the oldest scan reviewed elsewhere, a refused queue read standing
 * the scans alone. Drafts no row holds go; the same object when nothing moved or
 * went.
 */
export function customerDrafts(
  drafts: Readonly<Record<string, ReviewDraft>>,
  before: readonly ScanReviewGroup[],
  groups: readonly ScanReviewGroup[],
): Readonly<Record<string, ReviewDraft>> {
  const typed = new Set(Object.keys(drafts));
  const stoodIn = new Map<string, string>();
  for (const row of before) for (const id of row.scanIds) stoodIn.set(id, customerRowKey(row));
  const next: Record<string, ReviewDraft> = {};
  for (const group of groups) {
    const from = [...group.scanIds]
      .reverse()
      .map((id) => stoodIn.get(id))
      .find((was) => was !== undefined && typed.has(was));
    if (from !== undefined) next[customerRowKey(group)] = drafts[from];
  }
  const kept = Object.keys(next);
  const same = kept.length === typed.size && kept.every((key) => next[key] === drafts[key]);
  return same ? drafts : next;
}

/** A row confirms with a price above zero, when the lookup left one open a pick, and no catalogue dispute. */
export function reviewEntry(group: ScanReviewGroup, draft: ReviewDraft | undefined): ReviewEntry | null {
  if (needsCandidatePick(group.sample) || isCatalogDisputed(group)) return null;
  const priceCents = inputToCents(reviewPriceText(group, draft));
  if (priceCents === undefined || priceCents <= 0) return null;
  const quantity = isCustomerGroup(group) ? null : reviewQuantity(group, draft) || null;
  const unchanged = group.card !== null && priceCents === group.card.currentPriceCents;
  const approval = catalogApproval(group.sample, reviewApprovedCodes(group, draft)) ?? null;
  return { group, priceCents: unchanged ? null : priceCents, quantity, catalogApproval: approval };
}

/**
 * The rows "Confirm all priced" takes without a look each: owner scans of a
 * card already in the catalogue, or any owner row whose price was typed.
 * New and unvetted cards and customers' price checks want the reviewer's eye.
 */
export function batchEntry(group: ScanReviewGroup, draft: ReviewDraft | undefined): ReviewEntry | null {
  if (isCustomerGroup(group)) return null;
  if (group.bucket !== "exists" && draft?.price === undefined) return null;
  return reviewEntry(group, draft);
}
